import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import {
  EditableSlotGroup,
  EditableToken,
  RichTextContent,
  useContentToken,
  usePropertyField,
  useSlotGroup,
} from '@vibesites/runtime/react';

// ─── Slot group definitions ───────────────────────────────────────────────────

const applyCtaDef: SlotGroupDefinition = {
  label: 'Start Application Button',
  fields: {
    url: {
      label: 'URL',
      type: 'url',
      defaultField: 'property_apply_now_url',
      suggestedFields: ['property_apply_now_url'],
      defaultValue: '#',
    },
    label: {
      label: 'Button Text',
      type: 'text',
      defaultValue: 'Start Application',
    },
    _target: {
      label: 'Target',
      type: 'text',
      defaultValue: '_blank',
    },
  },
};

const resumeCtaDef: SlotGroupDefinition = {
  label: 'Resume Application Link',
  fields: {
    url: {
      label: 'URL',
      type: 'url',
      defaultField: 'property_apply_now_url',
      suggestedFields: ['property_apply_now_url'],
      defaultValue: '#',
    },
    label: {
      label: 'Link Text',
      type: 'text',
      defaultValue: 'I already started an application',
    },
    _target: {
      label: 'Target',
      type: 'text',
      defaultValue: '_blank',
    },
  },
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

function hasVal(v: string | null | undefined): v is string {
  return !!v && v !== 'false' && v !== 'null' && v !== 'undefined' && v.trim() !== '';
}

// ─── Component ────────────────────────────────────────────────────────────────

export function ApplyNowCTA() {
  // Editable text tokens
  const subtitle = useContentToken('applynowcta.subtitle');

  // Property data
  const propertyName = usePropertyField('property_name') as string | null;
  const addressLine = usePropertyField('property_address_line_one') as string | null;
  const city = usePropertyField('property_address_city') as string | null;
  const state = usePropertyField('property_address_state') as string | null;
  const zip = usePropertyField('property_address_zip') as string | null;
  const fullAddress = usePropertyField('property_full_address') as string | null;

  // Build city-line: "St. Petersburg, FL 33713"
  const cityLine = [
    hasVal(city) ? city : null,
    hasVal(state) ? state : null,
  ]
    .filter(Boolean)
    .join(', ') + (hasVal(zip) ? ` ${zip}` : '');

  // CTAs
  const applyCta = useSlotGroup('applynowcta.apply_cta', applyCtaDef);
  const resumeCta = useSlotGroup('applynowcta.resume_cta', resumeCtaDef);

  return (
    <section className="bg-white py-16 md:py-24">
      <div className="max-w-2xl mx-auto px-4 sm:px-6 text-center">

        {/* "Hi There," heading */}
        <RichTextContent
          path="applynowcta.heading"
          label="Heading"
          defaultValue="<p>Hi There,</p>"
          as="h2"
          className="font-heading text-4xl sm:text-5xl font-normal text-[var(--vibe-color-text)] mb-10 leading-tight"
        />

        {/* Subtitle */}
        <EditableToken
          path="applynowcta.subtitle"
          defaultValue="Your new home awaits at:"
          as="p"
          className="font-body text-base text-[var(--vibe-color-text)] mb-6"
        >
          {subtitle}
        </EditableToken>

        {/* Property name + address block */}
        <div className="mb-10">
          {hasVal(propertyName) && (
            <p className="font-body text-base font-semibold text-[var(--vibe-color-text)] mb-1">
              {propertyName}
            </p>
          )}
          {hasVal(addressLine) ? (
            <>
              <p className="font-body text-base text-[var(--vibe-color-text)]">
                {addressLine}
              </p>
              {cityLine.trim() && (
                <p className="font-body text-base text-[var(--vibe-color-text)]">
                  {cityLine}
                </p>
              )}
            </>
          ) : hasVal(fullAddress) ? (
            <p className="font-body text-base text-[var(--vibe-color-text)] whitespace-pre-line">
              {fullAddress}
            </p>
          ) : null}
        </div>

        {/* Start Application button */}
        <EditableSlotGroup
          groupPath="applynowcta.apply_cta"
          definition={applyCtaDef}
          className="block mb-6"
        >
          <a
            href={applyCta.url || '#'}
            target={applyCta._target || '_blank'}
            rel={(applyCta._target || '_blank') === '_blank' ? 'noopener noreferrer' : undefined}
            className="apply-now-cta-btn block w-full font-body font-bold text-lg text-primary-foreground text-center px-8 py-5 rounded-md transition-all duration-200"
          >
            {applyCta.label || 'Start Application'}
          </a>
        </EditableSlotGroup>

        {/* Resume link */}
        <EditableSlotGroup
          groupPath="applynowcta.resume_cta"
          definition={resumeCtaDef}
          className="block"
        >
          <a
            href={resumeCta.url || '#'}
            target={resumeCta._target || '_blank'}
            rel={(resumeCta._target || '_blank') === '_blank' ? 'noopener noreferrer' : undefined}
            className="inline-block font-body text-sm text-[var(--vibe-color-text)] underline underline-offset-4 hover:text-[var(--vibe-color-secondary)] transition-colors"
          >
            {resumeCta.label || 'I already started an application'}
          </a>
        </EditableSlotGroup>
      </div>

      <style>{`
        .apply-now-cta-btn {
          background-color: var(--vibe-color-primary);
        }
        .apply-now-cta-btn:hover {
          background-color: color-mix(in srgb, var(--vibe-color-primary) 85%, black);
          transform: translateY(-1px);
        }
      `}</style>
    </section>
  );
}

// ─── Content Schema ───────────────────────────────────────────────────────────

export const contentSchema: ComponentContentSchema = {
  slot: 'ApplyNowCTA',
  displayName: 'Apply Now CTA',
  tokens: {
    'applynowcta.heading': {
      label: 'Heading',
      type: 'richtext',
      defaultValue: '<p>Hi There,</p>',
    },
    'applynowcta.subtitle': {
      label: 'Subtitle',
      type: 'text',
      defaultValue: 'Your new home awaits at:',
    },
  },
  slotGroups: {
    'applynowcta.apply_cta': applyCtaDef,
    'applynowcta.resume_cta': resumeCtaDef,
  },
};

(ApplyNowCTA as unknown as { contentSchema: ComponentContentSchema }).contentSchema =
  contentSchema;

export default ApplyNowCTA;
